import type {
  InvoiceInconsistencyItem,
  InconsistencyMatchingResult,
} from 'src/core/modules/verification-of-payee/models';
import { isVerificationOfPayeeResultInvalid } from 'src/core/modules/verification-of-payee/utils';

import { type Transfer } from '../models';

export const formatNumber = (value: number, decimals: number) => {
  return (Math.round(value * 100) / 100).toFixed(decimals);
};

export const filterInvalidVerificationOfPayeeTransfers = (
  transfers: Transfer[],
) => {
  return transfers.filter((transfer) => {
    const verificationOfPayee = transfer.verificationOfPayee;
    return (
      verificationOfPayee &&
      isVerificationOfPayeeResultInvalid(
        verificationOfPayee.initialResult.matchingResult,
      ) &&
      !verificationOfPayee.isIgnored
    );
  });
};

type GroupedTransfersByCounterparty = {
  counterpartyId: string;
  iban: string;
  accountHolderName: string;
  count: number;
};

export const groupTransfersByCounterparty = (
  transfers: Transfer[],
): GroupedTransfersByCounterparty[] => {
  const mapTransfersByCounterparty = new Map<
    string,
    GroupedTransfersByCounterparty
  >();

  for (const transfer of transfers) {
    const { id: counterpartyId, bankAddress } = transfer.bill.counterparty;
    const iban = bankAddress?.iban;
    const accountHolderName = bankAddress?.accountHolderName;

    if (!iban || !accountHolderName) {
      continue;
    }

    const transfersGroup = mapTransfersByCounterparty.get(counterpartyId);

    if (transfersGroup) {
      transfersGroup.count += 1;
    } else {
      mapTransfersByCounterparty.set(counterpartyId, {
        counterpartyId,
        iban,
        accountHolderName,
        count: 1,
      });
    }
  }

  return Array.from(mapTransfersByCounterparty.values());
};

export const mapGroupedTransfersToInconsistencyItems = (
  groupedTransfers: GroupedTransfersByCounterparty[],
  getMatchingResult: (counterpartyId: string) => InconsistencyMatchingResult,
): InvoiceInconsistencyItem[] =>
  groupedTransfers.map((group) => ({
    id: group.counterpartyId,
    accountHolderName: group.accountHolderName,
    invoiceCount: group.count,
    matchingResult: getMatchingResult(group.counterpartyId),
  }));
